import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DecimalPipe } from '@angular/common';
import { formatBalanceDate } from 'shared/utils/balance-format.utils';
import { BalanceMoneyPipe } from 'shared/pipes/balance-money.pipe';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { AlertComponent } from 'shared/components/alert/alert.component';
import { TagComponent, TagComponentType } from 'shared/components/tag/tag.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { KorviuNoDataComponent } from 'shared/components/korviu-no-data/korviu-no-data.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { SortOrders } from 'shared/types/sort-order.enum';
import { BalanceService } from 'core/services/balance.service';
import {
  BalancePaymentAmount,
  ISpecialPaymentDetails,
  ISpecialPaymentMemberContribution,
  SpecialPaymentStatus
} from 'core/types/balance-transaction.interface';

type PaymentDetailsStatus = SpecialPaymentStatus | 'Open';

export function normalizePaymentStatus(status: string | null | undefined): PaymentDetailsStatus | null {
  switch (status?.toUpperCase()) {
    case 'OPEN':
      return 'Open';
    case 'PAID':
      return 'Paid';
    case 'PENDING':
      return 'Pending';
    case 'OVERDUE':
      return 'Overdue';
    case 'FAILED':
      return 'Failed';
    case 'HELD':
      return 'Held';
    case 'CARRIED':
      return 'Carried';
    case 'ADJUSTMENT':
      return 'Adjustment';
    default:
      return null;
  }
}

function paymentAmount(value: BalancePaymentAmount | null | undefined): number | null {
  if (value == null || (typeof value === 'string' && !value.trim())) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

@Component({
  selector: 'app-payment-details-v2',
  standalone: true,
  imports: [
    TranslateModule,
    RouterLink,
    GoBackComponent,
    AlertComponent,
    TagComponent,
    KorviuButtonComponent,
    KorviuLoaderComponent,
    KorviuNoDataComponent,
    KorviuListComponent,
    DecimalPipe,
    BalanceMoneyPipe
  ],
  templateUrl: './payment-details.component.html',
  styleUrl: './payment-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaymentDetailsComponent implements OnInit {
  private readonly _balanceService = inject(BalanceService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _translateService = inject(TranslateService);
  private readonly _reload = new Subject<void>();

  public readonly language = toSignal(this._translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this._translateService.getCurrentLang() || 'es'
  });
  public readonly isLoading = signal(true);
  public readonly loadError = signal(false);
  public readonly paymentDetails = signal<ISpecialPaymentDetails | null>(null);
  public readonly isCurrentMonth = signal(false);
  public readonly isProvisional = computed(
    () =>
      this.isCurrentMonth() ||
      this.paymentDetails()?.is_provisional === true ||
      normalizePaymentStatus(this.paymentDetails()?.status) === 'Open'
  );
  public readonly pageTitleKey = computed(() =>
    this.isProvisional() ? 'balance.currentMonthCommissions' : 'balance.paymentDetails'
  );

  // The backend resolves the displayed status, including Overdue and Held; the page never re-derives it.
  public readonly paymentStatus = computed<PaymentDetailsStatus>(() =>
    this.isProvisional() ? 'Open' : (normalizePaymentStatus(this.paymentDetails()?.status) ?? 'Pending')
  );
  public readonly paymentStatusTagType = computed<TagComponentType>(() => {
    switch (this.paymentStatus()) {
      case 'Paid':
        return 'success';
      case 'Pending':
      case 'Held':
        return 'warning';
      case 'Overdue':
      case 'Failed':
        return 'error';
      case 'Carried':
      case 'Open':
        return 'info';
      case 'Adjustment':
        return 'neutral';
    }
  });
  public readonly paymentStatusTranslationKey = computed(() => `balance.${this.paymentStatus().toLowerCase()}`);

  private readonly _breakdown = computed(() => this.paymentDetails()?.earnings_breakdown);
  public readonly settlementBreakdown = computed(() => this.paymentDetails()?.payment_breakdown);
  public readonly totalEarnings = computed(() => {
    const breakdown = this.settlementBreakdown();
    return paymentAmount(breakdown ? breakdown.earned : this._breakdown()?.total_earnings);
  });
  public readonly ownCardEarnings = computed(() => paymentAmount(this._breakdown()?.own_card_usage));
  public readonly communityEarnings = computed(() => paymentAmount(this._breakdown()?.community_earnings));
  public readonly carriedDeficitApplied = computed(() => paymentAmount(this._breakdown()?.carried_deficit_applied));
  public readonly totalPayout = computed(() => {
    const breakdown = this.settlementBreakdown();
    return breakdown
      ? paymentAmount(breakdown.your_payment)
      : (paymentAmount(this._breakdown()?.total_payout) ?? paymentAmount(this.paymentDetails()?.amount));
  });
  public readonly paymentResultAmount = computed(() =>
    this.settlementBreakdown() ? paymentAmount(this.paymentDetails()?.amount) : this.totalPayout()
  );
  public readonly paymentAlert = computed<{ type: 'info' | 'warning'; key: string } | null>(() => {
    switch (this.settlementBreakdown()?.alert_type) {
      case 'earnings_do_not_cover_returns':
        return { type: 'warning', key: 'balance.paymentReturnsNotCovered' };
      case 'earnings_cover_returns':
        return { type: 'info', key: 'balance.paymentReturnsCovered' };
      case 'carry_recovered':
        return { type: 'info', key: 'balance.paymentCarryRecovered' };
      default:
        return null;
    }
  });
  public readonly totalLeaves = computed(() => this.paymentDetails()?.total_leaves ?? null);

  public readonly paymentPeriodLabel = computed(() => this.formatDate(this.paymentDetails()?.period, 'period'));
  public readonly paymentDueDateLabel = computed(() => this.formatDate(this.paymentDetails()?.due_date));
  public readonly paymentPaidDateLabel = computed(() => this.formatDate(this.paymentDetails()?.paid_date));

  public readonly breakdownRows = computed(() => {
    const rows: { key: string; amount: number | null }[] = [
      { key: 'communityEarnings', amount: this.communityEarnings() },
      { key: 'ownCardUsage', amount: this.ownCardEarnings() },
      { key: 'totalEarnings', amount: this.totalEarnings() }
    ];
    const breakdown = this.settlementBreakdown();
    if (breakdown) {
      if (breakdown.returned_purchases != null) {
        const returned = paymentAmount(breakdown.returned_purchases);
        rows.push({ key: 'returnedPurchases', amount: returned === null ? null : -Math.abs(returned) });
      }
      if (breakdown.previous_balance != null) {
        rows.push({ key: 'previousBalance', amount: paymentAmount(breakdown.previous_balance) });
      }
      if (breakdown.carried_to_next_period != null) {
        rows.push({ key: 'carriedToNextPeriod', amount: paymentAmount(breakdown.carried_to_next_period) });
      }
    } else {
      const applied = this.carriedDeficitApplied();
      // Older candidates report the applied deficit as a magnitude; display it as a deduction.
      if (applied) rows.push({ key: 'paymentAdjustment', amount: applied > 0 ? -applied : applied });
    }
    return rows;
  });

  public readonly hasMemberContributions = computed(
    () => (this.paymentDetails()?.member_contributions?.members?.length ?? 0) > 0
  );
  // Per the design annotation, the total adds up whatever the search currently selects,
  // so it is recomputed for each page load rather than read from member_contributions.total.
  public readonly filteredContributionsTotal = signal<number | null>(null);

  // The endpoint returns every direct referral in one payload, so search, sorting and paging are client side.
  public readonly memberListConfig: IKorviuListConfig = {
    getFn: (pagination?: IPagination, filters?: IFilter[], sorting?: ISorting) =>
      this.loadMemberContributions(pagination, filters, sorting),
    itemType: KorviuListItemTypes.TOTAL_CONTRIBUTION,
    totalLocalizableName: 'balance.members',
    // member_contributions carries no user type, so there is nothing to filter on.
    hideFilterSection: true,
    filterSections: [],
    sortSections: [
      {
        localizableName: 'balance.contribution',
        options: [
          {
            localizableName: 'common.sortOption.highest',
            sortDirection: { sortBy: 'contribution', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.lowest',
            sortDirection: { sortBy: 'contribution', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'common.sortOption.name',
        options: [
          {
            localizableName: 'common.sortOption.A-Z',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.ASCEND }
          },
          {
            localizableName: 'common.sortOption.Z-A',
            sortDirection: { sortBy: 'full_name', sortOrder: SortOrders.DESCEND }
          }
        ]
      }
    ]
  };

  ngOnInit(): void {
    combineLatest([
      this._activatedRoute.paramMap.pipe(
        map(params => params.get('id')),
        distinctUntilChanged()
      ),
      this._reload.pipe(startWith(undefined))
    ])
      .pipe(
        switchMap(([id]) => {
          this.isCurrentMonth.set(id === 'current');
          this.paymentDetails.set(null);
          this.loadError.set(false);
          this.isLoading.set(true);
          if (id === 'current') {
            return this._balanceService.getCurrentPaymentDetails().pipe(catchError(() => of(null)));
          }
          if (!id || !/^\d+$/u.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0) {
            return of(null);
          }
          return this._balanceService.getPaymentDetails(Number(id)).pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(payment => {
        this.paymentDetails.set(payment);
        this.loadError.set(!payment);
        this.isLoading.set(false);
      });
  }

  public retry(): void {
    this._reload.next();
  }

  public loadMemberContributions(
    pagination?: IPagination,
    filters?: IFilter[],
    sorting?: ISorting
  ): Observable<IGetPaginatedResponse<ISpecialPaymentMemberContribution[]>> {
    const search = (filters?.find(filter => filter.name === 'search')?.value ?? '').trim().toLowerCase();
    const all = this.paymentDetails()?.member_contributions?.members ?? [];
    const selected = search ? all.filter(member => (member.full_name ?? '').toLowerCase().includes(search)) : [...all];
    this.sortMemberContributions(selected, sorting);
    this.filteredContributionsTotal.set(
      selected.reduce((sum, member) => sum + (paymentAmount(member.contribution) ?? 0), 0)
    );

    const page = pagination?.page ?? 1;
    const pageSize = pagination?.pageSize ?? selected.length;
    return of({
      links: { next: null, previous: null },
      count: selected.length,
      total_pages: pageSize ? Math.ceil(selected.length / pageSize) : 0,
      per_page: pageSize,
      results: selected.slice((page - 1) * pageSize, page * pageSize)
    });
  }

  private sortMemberContributions(members: ISpecialPaymentMemberContribution[], sorting?: ISorting): void {
    const direction = sorting?.sortOrder === SortOrders.ASCEND ? 1 : -1;
    if (sorting?.sortBy === 'full_name') {
      members.sort(
        (first, second) => (first.full_name ?? '').localeCompare(second.full_name ?? '', this.language()) * direction
      );
      return;
    }
    // Highest contribution first is the default the design shows before any sort is picked.
    members.sort(
      (first, second) =>
        ((paymentAmount(first.contribution) ?? 0) - (paymentAmount(second.contribution) ?? 0)) *
        (sorting ? direction : -1)
    );
  }

  private formatDate(value: string | null | undefined, kind: 'date' | 'period' = 'date'): string {
    return formatBalanceDate(value, this.language(), kind) || '—';
  }
}
